import React, { useEffect, useState } from 'react';

import { Form, FormikProvider, useFormik } from 'formik';
import { useSelector } from 'react-redux';
import * as yup from 'yup';
import { svg } from '../assets/svg';
import { components } from '../components';
import { showSuccessToast } from '../components/Toast';
import constants from '../helpers/constants';
import { hooks } from '../hooks';
import { Routes } from '../routes';
import apiService from '../service/ApiService';
import { RootState } from '../store';
import utils from '../helpers/utils';

const validationSchema = yup.object({
  firstName: yup.string().required('First name is Required.'),
  surName: yup.string().required('Last name is Required.'),
  phone: yup
    .string()
    .required('Phone number is required.')
    .min(10, 'Phone number must be at least 10 digits.')
    .matches(/^[0-9]+$/, 'Phone number must only contain digits.'),
  email: yup
    .string()
    .email('Email is not in a valid format.')
    .required('Email is required.'),
  postCode: yup
    .string()
    .required('Postcode is Required.')
    .min(4, 'Postcode should have 4 digits.')
    .matches(/^[0-9]+$/, 'Phone number must only contain digits.'),
  gender: yup.string().required('Choose your gender.'),
});

export const SignUp: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();

  const [opacity, setOpacity] = useState<number>(0);
  const [wantsUpdate, setWantsUpdate] = React.useState(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const currentLocation = useSelector((state: RootState) => state.appReducer.currentLocation);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 600);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      surName: '',
      phone: '',
      email: '',
      postCode: '',
      birthDate: '',
      promoCode: '',
      gender: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setErrors }) => {
      setSubmitting(true);
      setIsLoading(true);
      const apiData = {
        firstName: values.firstName,
        surName: values.surName,
        phone: values.phone,
        email: values.email,
        birthDate: values.birthDate !== null ? values.birthDate : null,
        postCode: values.postCode,
        promoCode: values.promoCode,
        gender: values.gender,
        latitude: currentLocation ? currentLocation.latitude : 0,
        longitude: currentLocation ? currentLocation.longitude : 0,
        wantsEmailUpdates: wantsUpdate,
      };

      const { data, error } = await apiService.userSignUpApiAsync(apiData);
      if (data) {
        // CjUtils.showToastMessage('Profile created successfully.', theme);
        showSuccessToast('OTP send successfully.');
        navigate(Routes.SendPhoneOtp, { state: { phoneNumber: values.phone } });
        // setIsSubmitting(false);
      } else if (error) {
        // showErrorToast(error);
        setErrors(error);
      }
      setSubmitting(true);
      setIsLoading(false);
    },
  });

  const renderHeader = (): JSX.Element => {
    return <components.Header leftLogo={true} title='Sign up' />;
  };

  const renderContent = (): JSX.Element => {
    return (
      <main className='scrollable container' style={{ paddingTop: 10, paddingBottom: 20 }}>
        <FormikProvider value={formik}>
          <Form autoComplete='off' noValidate onSubmit={formik.handleSubmit}>
            <section
              style={{
                backgroundColor: 'var(--white-color)',
                paddingLeft: 20,
                paddingRight: 20,
                minHeight: '84vh',
                maxHeight: '125vh',
                paddingTop: '20px',
                borderRadius: 10,
                marginTop: 10,
              }}
            >
              <div style={isMobile ? {} : { display: 'flex', flexDirection: 'row', gap: 10 }}>
                <components.Input
                  placeholder='First Name'
                  name='firstName'
                  containerStyle={{ marginBottom: 25, width: '100%' }}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  error={formik.errors.firstName}
                  touched={formik.touched.firstName}
                  leftIcon={<svg.UserSvg />}
                />
                <components.Input
                  placeholder='Last Name'
                  name='surName'
                  containerStyle={{ marginBottom: 25, width: '100%' }}
                  value={formik.values.surName}
                  onChange={formik.handleChange}
                  error={formik.errors.surName}
                  touched={formik.touched.surName}
                  leftIcon={<svg.UserSvg />}
                />
              </div>
              <components.Input
                placeholder='Email'
                name='email'
                leftIcon={<svg.MailSvg />}
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.errors.email}
                touched={formik.touched.email}
                // rightIcon={<svg.CheckSvg />}
                containerStyle={{ marginBottom: 25 }}
              />
              <components.Input
                placeholder='Phone Number'
                containerStyle={{ marginBottom: 25 }}
                name='phone'
                maxLength={10}
                inputMode={'numeric'}
                value={formik.values.phone}
                onKeyDown={utils.handleNumericKey}
                onChange={formik.handleChange}
                error={formik.errors.phone}
                touched={formik.touched.phone}
                leftIcon={<svg.PhoneSvg />}
              // rightIcon={<svg.CheckSvg />}
              />
              <div style={isMobile ? {} : { display: 'flex', flexDirection: 'row', gap: 10 }}>
                <components.Input
                  placeholder='Post Code'
                  leftIcon={<svg.KeySvg />}
                  name='postCode'
                  maxLength={4}
                  inputMode={'numeric'}
                  onKeyDown={utils.handleNumericKey}
                  value={formik.values.postCode}
                  onChange={formik.handleChange}
                  error={formik.errors.postCode}
                  touched={formik.touched.postCode}
                  // rightIcon={<svg.EyeOffSvg />}
                  containerStyle={{ marginBottom: 25, width: '100%' }}
                />
                <components.Input
                  type='date'
                  placeholder='birthDate'
                  name='birthDate'
                  leftIcon={<svg.UserSvg />}
                  value={formik.values.birthDate}
                  onChange={formik.handleChange}
                  error={formik.errors.birthDate}
                  touched={formik.touched.birthDate}
                  // rightIcon={<svg.EyeOffSvg />}
                  containerStyle={{ marginBottom: 25, width: '100%' }}
                />
              </div>
              <components.Input
                placeholder='Signup Code'
                name='promoCode'
                leftIcon={<svg.KeySvg />}
                maxLength={8}
                value={formik.values.promoCode}
                onChange={formik.handleChange}
                error={formik.errors.promoCode}
                touched={formik.touched.promoCode}
                // rightIcon={<svg.EyeOffSvg />}
                containerStyle={{ marginBottom: 25 }}
              />
              <components.Input
                type='radio'
                name='gender'
                options={constants.refGender}
                leftIcon={<svg.Gender />}
                value={formik.values.gender}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.errors.gender}
                touched={formik.touched.gender}
                // rightIcon={<svg.EyeOffSvg />}
                containerStyle={{ marginBottom: 20, flexWrap: 'wrap', display: 'flex' }}
              />
              <div
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  display: 'flex',
                  marginBottom: 15,
                }}
              >
                <input
                  type='checkbox'
                  id='email-updates'
                  name='email-updates'
                  className='input-field'
                  style={{ marginRight: 5, marginLeft: 5 }}
                  onChange={() => setWantsUpdate(!wantsUpdate)}
                />
                <label htmlFor='email-updates' className='t14'>
                  Yes, I would like to receive updates, offers and other
                  information by email.
                </label>
              </div>

              <components.Button
                text='SIGN UP'
                containerStyle={{ marginBottom: 20 }}
              />
              <div style={{ gap: 4 }} className='row-center'>
                <span className='t14'>Already have an account?</span>
                <span
                  className='t14 clickable'
                  style={{ color: 'var(--main-turquoise)' }}
                  onClick={() => {
                    navigate(Routes.SignIn);
                  }}
                >
                  Login.
                </span>
              </div>
            </section>
          </Form>
        </FormikProvider>
      </main>
    );
  };

  return (
    <div id='screen' style={{ opacity }}>
      {renderHeader()}
      {isLoading ? <components.Loader /> : renderContent()}
      {/* {renderFooter()} */}
    </div>
  );
};
